import './App.css'

import ApolloClient, { InMemoryCache, IntrospectionFragmentMatcher, Observable } from 'apollo-boost'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { Backdrop, CircularProgress, CssBaseline, Divider, Drawer, IconButton } from '@material-ui/core'
import React, { useContext, useEffect, useRef } from 'react'
import { ThemeProvider, createTheme, makeStyles, useTheme } from '@material-ui/core/styles'

import { ApolloProvider } from '@apollo/react-hooks'
import { ChevronLeft } from '@material-ui/icons'
import { HISTORY, Router } from './components/router'
import { Sidebar } from './components/sidebar'
import { SnackbarProvider } from 'material-ui-snackbar-provider'
import { ToastProvider } from '@vestaboard/installables'
import { TopNav } from './ui/TopNav'
import clsx from 'clsx'
import { drawerWidth } from './constants'
import { AUTH0_CLIENT_ID, AUTH0_DOMAIN, GRAPHQL_URL } from './env'
import { useGetViewer } from './api/useGetViewer'
import { usePostHog } from 'posthog-js/react'

function loginReturnPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function sanitizeReturnTo(returnTo: unknown): string {
  if (typeof returnTo !== 'string' || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return '/'
  }
  return returnTo
}

export const appBarHeight = 64

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex'
  },
  toolbar: theme.mixins.toolbar,
  drawerPaper: {
    width: drawerWidth
  },
  hide: {
    display: 'none'
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    [theme.breakpoints.up('sm')]: {
      width: drawerWidth,
      flexShrink: 0
    }
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end'
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginLeft: 0
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginLeft: drawerWidth
  }
}))

const GraphQLProvider: React.FC<React.PropsWithChildren> = props => {
  const { getAccessTokenSilently } = useAuth0()
  const posthog = usePostHog()

  const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData: {
      __schema: {
        types: [] // no types provided
      }
    }
  })

  const cache = new InMemoryCache({ fragmentMatcher })

  const client = new ApolloClient({
    uri: GRAPHQL_URL,
    cache,
    request: async operation => {
      const sessionId = posthog?.get_session_id()
      operation.setContext({
        headers: {
          'X-Vestaboard-Token': await getAccessTokenSilently(),
          ...(sessionId ? { 'X-PostHog-Session-Id': sessionId } : {})
        }
      })
    },
    onError: ({ graphQLErrors, operation, forward }) => {
      const hasTokenRefreshRequired = graphQLErrors?.some(
        error => error.extensions?.code === 'TOKEN_REFRESH_REQUIRED'
      )

      if (hasTokenRefreshRequired) {
        return new Observable(observer => {
          getAccessTokenSilently({ cacheMode: 'off' })
            .then(token => {
              operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
                headers: { ...headers, 'X-Vestaboard-Token': token }
              }))
              forward(operation).subscribe(observer)
            })
            .catch(observer.error.bind(observer))
        })
      }
    }
  })

  return <ApolloProvider client={client}>{props.children}</ApolloProvider>
}

const theme = createTheme({})

export const Providers: React.FC<React.PropsWithChildren> = props => {
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN || ''}
      clientId={AUTH0_CLIENT_ID || ''}
      cacheLocation='localstorage'
      useRefreshTokens
      useRefreshTokensFallback={false}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: `https://${AUTH0_DOMAIN}/api/v2/`,
        scope: `openid profile email offline_access`
      }}
      onRedirectCallback={appState => {
        HISTORY.replace(sanitizeReturnTo(appState?.returnTo))
      }}>
      <ThemeProvider theme={theme}>
        <ToastProvider>
            <SnackbarProvider SnackbarProps={{ autoHideDuration: 4000 }}>
              <DrawerContextProvider>
                <CssBaseline />
                <GraphQLProvider>{props.children}</GraphQLProvider>
              </DrawerContextProvider>
            </SnackbarProvider>
          </ToastProvider>
      </ThemeProvider>
    </Auth0Provider>
  )
}

interface IDrawerContext {
  setDrawerOpen: (v: boolean) => any
  drawerOpen: boolean
}

const DrawerContext = React.createContext<IDrawerContext>({ drawerOpen: true, setDrawerOpen: () => {} })

const DrawerContextProvider: React.FC<React.PropsWithChildren> = props => {
  const [drawerOpen, setDrawerOpen] = React.useState(true)

  return (
    <DrawerContext.Provider
      value={{
        drawerOpen,
        setDrawerOpen
      }}>
      {props.children}
    </DrawerContext.Provider>
  )
}

export const useDrawerContext = () => useContext(DrawerContext)

const AuthenticatedApp = () => {
  const { logout } = useAuth0()

  const theme = useTheme()
  const classes = useStyles(theme)
  const { drawerOpen, setDrawerOpen } = useDrawerContext()
  const { data, error, loading } = useGetViewer()
  const posthog = usePostHog()
  const logoutCalledRef = useRef(false)

  const handleLogout = () => {
    posthog?.reset()
    logout({ logoutParams: { returnTo: window.location.origin } })
  }

  // Log out if the viewer is not a superadmin
  useEffect(() => {
    if (error && !logoutCalledRef.current) {
      logoutCalledRef.current = true
      handleLogout()
    }
  }, [error, logout])

  useEffect(() => {
    const personId = data?.viewer?.account?.person.id
    if (posthog && personId && posthog.get_distinct_id() !== personId) {
      posthog.identify(personId)
    }
  }, [data, posthog])

  if (loading || !data?.viewer?.account?.person.id) {
    return (
      <Backdrop open>
        <CircularProgress color='inherit' />
      </Backdrop>
    )
  }

  return (
    <div className={classes.root}>
      <CssBaseline />
      <TopNav logout={handleLogout} setDrawerOpen={setDrawerOpen} drawerOpen={drawerOpen} />
      <Drawer
        variant='persistent'
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        classes={{
          paper: classes.drawerPaper
        }}
        ModalProps={{
          keepMounted: true // Better open performance on mobile.
        }}>
        <div className={classes.drawerHeader}>
          <IconButton onClick={() => setDrawerOpen(false)}>
            <ChevronLeft />
          </IconButton>
        </div>
        <Divider />
        <Sidebar />
      </Drawer>
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: drawerOpen
        })}>
        <div className={classes.toolbar} />
        <Router />
      </main>
    </div>
  )
}

const App: React.FC = () => {
  const { isLoading: auth0Loading, isAuthenticated, loginWithRedirect } = useAuth0()

  useEffect(() => {
    if (!auth0Loading && !isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: loginReturnPath() } })
    }
  }, [auth0Loading, isAuthenticated, loginWithRedirect])

  if (auth0Loading) {
    return (
      <Backdrop open>
        <CircularProgress color='inherit' />
      </Backdrop>
    )
  }

  if (!isAuthenticated) {
    return (
      <Backdrop open>
        <CircularProgress color='inherit' />
      </Backdrop>
    )
  }

  return <AuthenticatedApp />
}

export default App
